import { find as linkify } from 'linkifyjs';
import { uniqueId } from 'lodash';
import { KeyedMutator } from 'swr';

import { PromiseService } from '@/utils/services/PromiseService';
import { TimeService } from '@/utils/services/TimeService';

import { DMConversationMessagesResponse } from '@/utils/hooks/swr/conversations/useDMConversationMessages';

import { post } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

import {
    DMConversation,
    DMConversationMessage,
    MessageFilter
} from '@/components/pages/conversations/SocialInbox/types';

import {
    DM_CONVERSATION_MESSAGES,
    DM_CONVERSATIONS,
    DM_MESSAGE,
    MARK_DM_CONVERSATION_MESSAGES_AS_READ,
    MARK_DM_CONVERSATION_MESSAGES_AS_UNREAD
} from '@/lib/routes/apiEndpoints';
import { DM_STATUS, DM_TYPES } from '@/components/pages/conversations/SocialInbox/const';

class DirectMessageService {
    hydrateConversations(
        conversations: DMConversation[],
        newMessage: DMConversationMessage,
        isPartOfOpenedConversation: boolean
    ): DMConversation[] {
        return conversations.map((conversation) => {
            if (conversation.meta_conversation_id === newMessage.meta_conversation_id) {
                return {
                    ...conversation,
                    last_message: newMessage,
                    // Mark as unread logic
                    seen_at: isPartOfOpenedConversation ? conversation.seen_at : null
                };
            }
            return conversation;
        });
    }

    hydrateMessages(
        previousMessages: DMConversationMessagesResponse[],
        newMessageData: DMConversationMessage
    ) {
        // If there are no previous messages, create a new message group
        if (!previousMessages?.length) {
            return [{ data: [newMessageData], cursor: null }];
        }

        // Hydrating UI only message (not yet on the server)
        if (newMessageData.tempId || newMessageData.id) {
            const pageIndex = previousMessages.findIndex((messageGroup) =>
                messageGroup.data.some((message) => {
                    // If the new message has a tempId, look for it by tempId
                    if (newMessageData.tempId && message.tempId === newMessageData.tempId) {
                        return true;
                    }
                    // If the new message has an id, look for it by id
                    if (newMessageData.id && message.id === newMessageData.id) {
                        return true;
                    }
                    return false;
                })
            );

            if (pageIndex !== -1) {
                return previousMessages.map((page, index) => {
                    if (index === pageIndex) {
                        return {
                            ...page,
                            data: page.data.map((message) => {
                                // If the new message has a tempId, replace by tempId
                                if (
                                    newMessageData.tempId &&
                                    message.tempId === newMessageData.tempId
                                ) {
                                    return newMessageData;
                                }
                                // If the new message has an id, replace by id
                                if (newMessageData.id && message.id === newMessageData.id) {
                                    return newMessageData;
                                }
                                return message;
                            })
                        };
                    }
                    return page;
                });
            }
        }

        // If the message doesn't exist, add it as a new message group
        return [...previousMessages, { data: [newMessageData], cursor: null }];
    }

    sendMessage = async (
        message: string,
        socialProfileId: number,
        conversationId: string,
        updateMessageStatus: (message: DMConversationMessage) => void,
        retryMessageId?: string
    ) => {
        const trimmedMessage = message.trim();
        if (!trimmedMessage || !socialProfileId) {
            return;
        }
        const tempId = retryMessageId || uniqueId('new-dm-message-');
        const messageToSend = {
            content: trimmedMessage,
            meta_conversation_id: conversationId,
            type: DM_TYPES.TEXT,
            tempId,
            sent_at: new Date().toISOString(),
            is_from_me: true
        } as unknown as DMConversationMessage;

        if (!retryMessageId) {
            updateMessageStatus({ ...messageToSend, status: DM_STATUS.SENDING });
        }

        try {
            const response = await post(
                injectEndpointUrlParams(DM_MESSAGE, {
                    SPID: socialProfileId,
                    CONVERSATION_ID: conversationId
                }),
                messageToSend
            );

            messageToSend.id = response?.data?.message_id;

            updateMessageStatus({ ...messageToSend, status: DM_STATUS.SENT });
        } catch (error) {
            console.error('Failed to send message:', error);

            updateMessageStatus({ ...messageToSend, status: DM_STATUS.FAILED });
        }
    };

    getUserInitials = (userName: string) => {
        if (!userName) return 'U';

        // For space-separated names
        if (userName.includes(' ')) {
            return userName
                .split(' ')
                .map((part) => part.charAt(0).toUpperCase())
                .join('');
        }

        // For camelCase or PascalCase names
        const camelCaseMatches = userName.match(/[A-Z][a-z]*/g);
        if (camelCaseMatches && camelCaseMatches.length > 0) {
            const firstChar = userName.charAt(0).toUpperCase();
            const restInitials = camelCaseMatches.map((part) => part.charAt(0)).join('');
            return (
                firstChar +
                (firstChar === restInitials.charAt(0) ? restInitials.slice(1) : restInitials)
            );
        }

        return userName.charAt(0).toUpperCase();
    };

    toggleConversationReadUnread = async (
        socialProfileId: number,
        messageIds: string[],
        isRead: boolean
    ) => {
        if (!socialProfileId || !messageIds || messageIds.length === 0) {
            return false;
        }

        const endpoint = isRead
            ? MARK_DM_CONVERSATION_MESSAGES_AS_UNREAD
            : MARK_DM_CONVERSATION_MESSAGES_AS_READ;

        const url = injectEndpointUrlParams(endpoint, { SPID: socialProfileId });

        try {
            await post(url, { ids: messageIds });
            return true;
        } catch (error) {
            console.error(`Failed to mark conversation as ${isRead ? 'unread' : 'read'}:`, error);
            return false;
        }
    };

    mutateConversationReadStatus = (
        mutateMessages: KeyedMutator<DMConversation[]>,
        messageIds: string[],
        isRead: boolean
    ) => {
        mutateMessages(
            (prev) =>
                prev.map((c: DMConversation) =>
                    messageIds.includes(c.meta_conversation_id?.toString())
                        ? { ...c, seen_at: isRead ? null : new Date().toISOString() }
                        : c
                ),
            { revalidate: false }
        );
    };

    /**
     * Threshold in seconds for grouping messages together
     */
    private readonly MESSAGE_GROUP_THRESHOLD_SECONDS = 30;

    /**
     * Determines if the current message should be grouped with the previous message
     * Messages are grouped if:
     * - They are from the same sender
     * - They are within 30 seconds of each other
     */
    shouldGroupWithPrevious = (
        currentMessage: DMConversationMessage,
        previousMessage: DMConversationMessage | null
    ): boolean => {
        if (!previousMessage) return false;

        // Different sender - don't group
        if (currentMessage.is_from_me !== previousMessage.is_from_me) return false;

        // Check time difference
        const currentTime = new Date(currentMessage.sent_at).getTime();
        const previousTime = new Date(previousMessage.sent_at).getTime();
        const timeDiffSeconds = Math.abs(currentTime - previousTime) / 1000;

        return timeDiffSeconds < this.MESSAGE_GROUP_THRESHOLD_SECONDS;
    };

    parseLinks = (text: string) => {
        if (!text) return [];

        const links = linkify(text);
        const result = [];
        let lastIndex = 0;

        links.forEach((link) => {
            // Add text before the link
            if (link.start > lastIndex) {
                result.push({
                    text: text.slice(lastIndex, link.start),
                    isUrl: false,
                    url: undefined
                });
            }
            // Add the link
            result.push({
                text: link.value,
                isUrl: link.isLink,
                url: link.href
            });
            lastIndex = link.end;
        });

        // Add remaining text after last link
        if (lastIndex < text.length) {
            result.push({
                text: text.slice(lastIndex),
                isUrl: false,
                url: undefined
            });
        }

        return result;
    };

    filterConversations = (conversations: DMConversation[], filterState: MessageFilter) => {
        return conversations
            .filter((conversation) => {
                // Filter by search query
                if (filterState.search) {
                    const searchLower = filterState.search.toLowerCase();
                    const matchesLastMessageSearch = conversation?.last_message?.content
                        ?.toLowerCase()
                        ?.includes(searchLower);
                    const matchesSenderSearch = conversation?.initiator?.name
                        ?.toLowerCase()
                        ?.includes(searchLower);

                    if (!matchesLastMessageSearch && !matchesSenderSearch) return false;
                }

                // Filter by read/unread status
                if (filterState.filter === 'unread') return !conversation?.seen_at;
                if (filterState.filter === 'read') return !!conversation?.seen_at;

                return true;
            })
            .sort((a, b) => {
                return TimeService.sortDates(
                    a?.last_message?.sent_at,
                    b?.last_message?.sent_at,
                    filterState.sort
                );
            });
    };

    /**
     * Initiates a conversation fetch job with proper synchronization.
     * If called multiple times simultaneously for the same social profile ID, all callers will wait for the same promise.
     */
    initiateConversationFetchJob = async (socialProfileId: number): Promise<void> => {
        const key = `conversation-fetch-job-${socialProfileId}`;
        return PromiseService.deduplicate(key, () =>
            this.performConversationFetchJob(socialProfileId)
        );
    };

    /**
     * Initiates a conversation messages fetch job with proper synchronization.
     * If called multiple times simultaneously for the same social profile ID, all callers will wait for the same promise.
     */
    initiateConversationMessageFetchJob = async (
        socialProfileId: number,
        conversationId: string
    ): Promise<void> => {
        const key = `conversation-message-fetch-job-${socialProfileId}-${conversationId}`;
        return PromiseService.deduplicate(key, () =>
            this.performConversationMessageFetchJob(socialProfileId, conversationId)
        );
    };

    /**
     * Performs the actual conversation fetch job
     */
    private readonly performConversationFetchJob = async (
        socialProfileId: number
    ): Promise<void> => {
        const url = injectEndpointUrlParams(DM_CONVERSATIONS, { SPID: socialProfileId });
        await post(url);
    };

    private readonly performConversationMessageFetchJob = async (
        socialProfileId: number,
        conversationId: string
    ): Promise<void> => {
        const url = injectEndpointUrlParams(DM_CONVERSATION_MESSAGES, {
            SPID: socialProfileId,
            CONVERSATION_ID: conversationId
        });
        await post(url);
    };
}

const directMessageService = new DirectMessageService();

export { directMessageService as DirectMessageService };
